import React, { useState } from 'react';
import {
  MdCloud, MdNotificationsActive, MdSms, MdRefresh, MdCheckCircle, MdWarning, MdError,
  MdPhoneAndroid, MdStorage, MdPublic, MdAccountBalanceWallet, MdSmartToy,
} from 'react-icons/md';
import api from '../../ui/api';
import useData from '../useData';
import PageShell, { Button, Notice } from '../../ui/PageShell';
import Card, { StatCard, StatGrid } from '../../ui/Card';
import './services.css';

/**
 * الخدمات المرتبطة — every outside service the system runs on, in one
 * place: Cloudflare usage against the free plan, website traffic and
 * cache, phones registered with OneSignal, ClickSend balance and SMS.
 * Data: GET /api/v1/system/services (api/src/routes/services.js).
 */

const n = (v) => Number(v || 0).toLocaleString('en-US');
const bytes = (b) => {
  const v = Number(b || 0);
  if (v >= 1024 ** 3) return `${(v / 1024 ** 3).toFixed(2)} GB`;
  if (v >= 1024 ** 2) return `${(v / 1024 ** 2).toFixed(1)} MB`;
  if (v >= 1024) return `${Math.round(v / 1024)} KB`;
  return `${v} B`;
};
const when = (iso) => (iso
  ? new Date(iso).toLocaleString('ar-SY', { dateStyle: 'medium', timeStyle: 'short' })
  : '—');
const pct = (a, b) => (b ? Math.min(100, (a / b) * 100) : 0);
const usd = (v) => (v == null ? '—' : '$' + Number(v).toFixed(v > 0 && v < 1 ? 4 : 2));
const ROLE = { admin: 'مدير', register: 'موجه', teacher: 'معلم' };

/** Usage against a limit: bar + numbers in text; colour only near the limit. */
export function Meter({ label, used, limit, format = n, hint }) {
  const p = pct(used, limit);
  const state = p >= 90 ? 'is-critical' : p >= 70 ? 'is-warning' : '';
  return (
    <div className={`sv-meter ${state}`}>
      <div className="sv-meter-head">
        <span className="sv-meter-label">{label}</span>
        <span className="sv-meter-value">
          <b>{format(used)}</b> / {format(limit)}
          <small>{p < 0.1 && used > 0 ? '<0.1' : p.toFixed(p < 10 ? 1 : 0)}%</small>
        </span>
      </div>
      <div className="sv-meter-track" role="progressbar" aria-valuenow={Math.round(p)} aria-valuemin={0} aria-valuemax={100} aria-label={label}>
        <div className="sv-meter-fill" style={{ width: `${Math.max(p, used > 0 ? 1 : 0)}%` }} />
      </div>
      {hint && <small className="sv-meter-hint">{hint}</small>}
    </div>
  );
}

/** Every calendar day of the last [span] days, missing ones as zero, so the
 *  time axis is honest. */
function fillDays(days, span = 30) {
  const by = new Map(days.map((d) => [d.date, d]));
  const out = [];
  for (let i = span - 1; i >= 0; i -= 1) {
    const date = new Date(Date.now() - i * 86400000).toISOString().slice(0, 10);
    out.push(by.get(date) || { date });
  }
  return out;
}

/** Daily bars (one series) with a hover tooltip. */
export function DayBars({ days: raw, value: rawValue, label, format = n }) {
  const [hover, setHover] = useState(null);
  const days = fillDays(raw);
  const value = (d) => rawValue(d) || 0;
  const max = Math.max(1, ...days.map((d) => value(d)));
  if (!days.length) return <p className="sv-muted">لا توجد بيانات بعد.</p>;
  const h = hover != null ? days[hover] : null;
  return (
    <div className="sv-bars-wrap">
      <div className="sv-bars" onMouseLeave={() => setHover(null)} role="img" aria-label={label}>
        {days.map((d, i) => (
          <button
            key={d.date}
            type="button"
            className={`sv-bar${hover === i ? ' is-hover' : ''}`}
            onMouseEnter={() => setHover(i)}
            onFocus={() => setHover(i)}
            aria-label={`${d.date}: ${format(value(d))}`}
          >
            <span style={{ height: `${Math.max(2, (value(d) / max) * 100)}%` }} />
          </button>
        ))}
      </div>
      <div className="sv-bars-foot">
        <span dir="ltr">{days[0].date}</span>
        <span className="sv-bars-tip">{h ? <><b dir="ltr">{h.date}</b> — {format(value(h))} {label}</> : `${label} — مرّر على العمود`}</span>
        <span dir="ltr">{days[days.length - 1].date}</span>
      </div>
    </div>
  );
}

export function Status({ s }) {
  if (!s) return null;
  if (s.ok) return <span className="sv-status is-ok"><MdCheckCircle /> يعمل</span>;
  if (s.configured === false) return <span className="sv-status is-warn"><MdWarning /> غير مربوط</span>;
  return <span className="sv-status is-bad"><MdError /> خطأ</span>;
}

export default function Services() {
  const { data, loading, error, reload } = useData(() => api.servicesStatus(), []);
  const cf = data?.cloudflare;
  const os = data?.onesignal;
  const cs = data?.clicksend;
  const sms = data?.sms;
  const lim = cf?.limits || {};
  const wai = data?.ai?.workers_ai;
  const asst = data?.ai?.assistant;
  const lowCredit = cs?.ok && cs.account.low_credit_amount != null && cs.account.balance <= cs.account.low_credit_amount;

  return (
    <PageShell
      title="الخدمات المرتبطة"
      description={data ? `آخر تحديث: ${when(data.generated_at)}` : 'Cloudflare · الذكاء الاصطناعي · OneSignal · ClickSend · الرسائل'}
      actions={<Button variant="secondary" onClick={reload} loading={loading}>{!loading && <MdRefresh size={16} />} تحديث</Button>}
    >
      {error && <Notice tone="danger" title="تعذّر التحميل">{error.message}</Notice>}
      {!data && loading && <Card><p className="sv-muted">جارٍ جمع البيانات من الخدمات…</p></Card>}

      {data && (
        <>
          <StatGrid>
            <StatCard
              icon={<MdCloud />}
              tone={cf?.ok ? 'info' : 'warning'}
              label="طلبات الخادم اليوم"
              value={cf?.ok ? n(cf.workers.requests_today) : '—'}
              hint={cf?.ok ? `من ${n(lim.worker_requests_per_day)} يومياً في الخطة المجانية` : 'Cloudflare غير مربوط'}
            />
            <StatCard
              icon={<MdPhoneAndroid />}
              tone={os?.ok ? 'success' : 'warning'}
              label="هواتف تستقبل الإشعارات"
              value={os?.ok ? `${n(os.app.messageable_players)} / ${n(os.app.players)}` : '—'}
              hint="OneSignal"
            />
            <StatCard
              icon={<MdAccountBalanceWallet />}
              tone={!cs?.ok ? 'warning' : lowCredit ? 'danger' : 'success'}
              label="رصيد ClickSend"
              value={cs?.ok ? `${cs.account.balance.toFixed(2)} ${cs.account.currency || ''}` : '—'}
              hint={lowCredit ? 'الرصيد منخفض — اشحن الحساب' : 'رسائل الأرقام غير السورية'}
            />
            <StatCard
              icon={<MdSms />}
              tone="info"
              label="رسائل SMS (30 يوماً)"
              value={sms?.ok ? n(sms.last_30d.reduce((a, p) => a + p.sent, 0)) : '—'}
              hint={sms?.ok ? `فشل: ${n(sms.last_30d.reduce((a, p) => a + p.failed, 0))}` : ''}
            />
            <StatCard
              icon={<MdSmartToy />}
              tone={wai?.ok ? 'info' : 'warning'}
              label="أسئلة «اسأل مشكاة» اليوم"
              value={asst?.ok ? n(asst.today.data + asst.today.help) : '—'}
              hint={wai?.ok ? `تكلفة 30 يوماً: ${usd((wai.last_30d.cost_usd || 0) + (asst?.claude?.cost_30d_usd || 0))}` : ''}
            />
          </StatGrid>

          {/* ---------------------------------------------------- Cloudflare */}
          <Card title={<span className="sv-title"><MdCloud /> Cloudflare — الخادم وقاعدة البيانات والموقع <Status s={cf} /></span>}>
            {cf?.configured === false ? (
              <Notice tone="warning" title="أضف مفتاح Cloudflare للقراءة فقط">
                في Cloudflare: My Profile ← API Tokens ← Create Token ← Custom token، بالصلاحيات:
                <b> Account › Account Analytics › Read</b> و <b>Zone › Analytics › Read</b> (للنطاق mtzedu.com).
                ثم في مجلد api: <code dir="ltr">npx wrangler secret put CF_API_TOKEN</code>
              </Notice>
            ) : !cf?.ok ? (
              <Notice tone="danger" title="تعذّر الاتصال بـ Cloudflare">{cf?.error}</Notice>
            ) : (
              <>
                <h4 className="sv-sub">الاستهلاك مقارنة بحدود الخطة المجانية</h4>
                <div className="sv-meters">
                  <Meter label="طلبات الخادم (اليوم)" used={cf.workers.requests_today} limit={lim.worker_requests_per_day} />
                  <Meter label="قراءة صفوف قاعدة البيانات (اليوم)" used={cf.d1.today.rows_read} limit={lim.d1_rows_read_per_day} />
                  <Meter label="كتابة صفوف قاعدة البيانات (اليوم)" used={cf.d1.today.rows_written} limit={lim.d1_rows_written_per_day} />
                  <Meter label="حجم قاعدة البيانات" used={cf.d1.size_bytes} limit={lim.d1_storage_bytes} format={bytes} />
                  <Meter
                    label="تخزين النسخ الاحتياطية (R2)"
                    used={cf.r2.buckets.reduce((a, b) => a + b.bytes, 0)}
                    limit={lim.r2_storage_bytes}
                    format={bytes}
                    hint={`${n(cf.r2.buckets.reduce((a, b) => a + b.objects, 0))} ملف`}
                  />
                  <Meter label="عمليات كتابة R2 (هذا الشهر)" used={cf.r2.class_a_this_month} limit={lim.r2_class_a_per_month} />
                  <Meter label="عمليات قراءة R2 (هذا الشهر)" used={cf.r2.class_b_this_month} limit={lim.r2_class_b_per_month} />
                </div>

                <h4 className="sv-sub">الخوادم — آخر 24 ساعة</h4>
                <div className="sv-table-wrap">
                  <table className="sv-table">
                    <thead><tr><th>الخادم</th><th>الطلبات</th><th>الأخطاء</th><th>طلبات خارجية</th><th>وقت المعالج (وسيط / أقصى)</th></tr></thead>
                    <tbody>
                      {cf.workers.last_24h.map((w) => (
                        <tr key={w.script}>
                          <td dir="ltr" className="sv-code">{w.script}</td>
                          <td>{n(w.requests)}</td>
                          <td className={w.errors ? 'sv-bad' : ''}>{n(w.errors)}</td>
                          <td>{n(w.subrequests)}</td>
                          <td dir="ltr">{w.cpu_ms_p50} ms / {w.cpu_ms_p99} ms</td>
                        </tr>
                      ))}
                      {!cf.workers.last_24h.length && <tr><td colSpan={5} className="sv-muted">لا طلبات</td></tr>}
                    </tbody>
                  </table>
                </div>

                <h4 className="sv-sub">قاعدة البيانات</h4>
                <div className="sv-facts">
                  <div><small>استعلامات قراءة اليوم</small><b>{n(cf.d1.today.read_queries)}</b></div>
                  <div><small>استعلامات كتابة اليوم</small><b>{n(cf.d1.today.write_queries)}</b></div>
                  <div><small>صفوف مقروءة (30 يوماً)</small><b>{n(cf.d1.last_30d.rows_read)}</b></div>
                  <div><small>صفوف مكتوبة (30 يوماً)</small><b>{n(cf.d1.last_30d.rows_written)}</b></div>
                </div>

                <h4 className="sv-sub"><MdPublic /> حركة الموقع mtzedu.com — آخر 30 يوماً</h4>
                <div className="sv-facts">
                  <div><small>الطلبات</small><b>{n(cf.traffic.requests)}</b></div>
                  <div><small>من الذاكرة المؤقتة (Cache)</small><b>{pct(cf.traffic.cached_requests, cf.traffic.requests).toFixed(0)}%</b><em>{n(cf.traffic.cached_requests)} طلب</em></div>
                  <div><small>البيانات المنقولة</small><b><bdi dir="ltr">{bytes(cf.traffic.bytes)}</bdi></b><em>منها من الذاكرة المؤقتة <bdi dir="ltr">{bytes(cf.traffic.cached_bytes)}</bdi></em></div>
                  <div><small>مشاهدات صفحات</small><b>{n(cf.traffic.page_views)}</b></div>
                  <div className={cf.traffic.threats ? 'is-bad' : ''}><small>تهديدات تم صدّها</small><b>{n(cf.traffic.threats)}</b></div>
                </div>
                <DayBars days={cf.traffic.days} value={(d) => d.requests} label="طلب" />

                <h4 className="sv-sub">طلبات الخادم يومياً</h4>
                <DayBars days={cf.workers.days} value={(d) => d.requests} label="طلب للخادم" />
              </>
            )}
          </Card>

          {/* ------------------------------------------------------------ AI */}
          <Card title={<span className="sv-title"><MdSmartToy /> الذكاء الاصطناعي — «اسأل مشكاة» <Status s={wai} /></span>}>
            <h4 className="sv-sub">أسئلة البيانات — Cloudflare Workers AI</h4>
            {wai?.configured === false ? (
              <Notice tone="warning" title="Cloudflare غير مربوط">يُقرأ الاستهلاك بمفتاح CF_API_TOKEN نفسه.</Notice>
            ) : !wai?.ok ? (
              <Notice tone="danger" title="تعذّر قراءة الاستهلاك">{wai?.error}</Notice>
            ) : (
              <>
                <div className="sv-meters">
                  <Meter
                    label="وحدات المعالجة اليوم (Neurons)"
                    used={wai.today.neurons}
                    limit={wai.free_neurons_per_day}
                    hint={wai.free_questions_left_today != null
                      ? `يكفي المجاني لنحو ${n(wai.free_questions_left_today)} سؤالاً آخر اليوم`
                      : 'المجاني يتجدد كل يوم'}
                  />
                </div>
                <div className="sv-facts">
                  <div><small>أسئلة اليوم</small><b>{n(wai.today.requests)}</b></div>
                  <div><small>أسئلة (30 يوماً)</small><b>{n(wai.last_30d.requests)}</b></div>
                  <div><small>متوسط السؤال</small><b>{wai.neurons_per_request != null ? `${n(wai.neurons_per_request)} وحدة` : '—'}</b></div>
                  <div><small>الكلمات المقروءة / المكتوبة (Tokens)</small><b><bdi dir="ltr">{n(wai.last_30d.input_tokens)} / {n(wai.last_30d.output_tokens)}</bdi></b></div>
                  <div><small>التكلفة (30 يوماً)</small><b><bdi dir="ltr">{usd(wai.last_30d.cost_usd)}</bdi></b><em>{wai.last_30d.cost_usd ? 'تقديرية بسعر الخطة المدفوعة' : 'ضمن المجاني'}</em></div>
                  <div><small>النموذج</small><b dir="ltr" className="sv-code">{wai.model.replace('@cf/', '')}</b></div>
                </div>
                <p className="sv-muted sv-note">
                  السعر: أول <bdi dir="ltr">{n(wai.free_neurons_per_day)}</bdi> وحدة كل يوم مجاناً، وبعدها <bdi dir="ltr">{usd(wai.usd_per_1k_neurons)}</bdi> لكل 1,000 وحدة
                  (في خطة Cloudflare المدفوعة؛ في المجانية تتوقف أسئلة البيانات حتى اليوم التالي).
                </p>
                <DayBars days={wai.days} value={(d) => d.neurons} label="وحدة" />
              </>
            )}

            <h4 className="sv-sub">مساعد الاستخدام — Claude (Anthropic)</h4>
            {!asst?.ok ? (
              <Notice tone="danger" title="تعذّر القراءة">{asst?.error}</Notice>
            ) : (
              <>
                {!asst.claude.configured && (
                  <Notice tone="warning" title="مفتاح Claude غير مضاف">
                    المساعد يجيب الآن من «دليل الاستخدام» فقط، بلا تكلفة. للإضافة في مجلد api: <code dir="ltr">npx wrangler secret put ANTHROPIC_API_KEY</code>
                  </Notice>
                )}
                <div className="sv-facts">
                  <div><small>أسئلة (30 يوماً)</small><b>{n(asst.claude.questions_30d)}</b></div>
                  <div><small>الكلمات المقروءة / المكتوبة</small><b><bdi dir="ltr">{n(asst.claude.tokens_30d.input)} / {n(asst.claude.tokens_30d.output)}</bdi></b><em>من الذاكرة المؤقتة: <bdi dir="ltr">{n(asst.claude.tokens_30d.cache_read)}</bdi></em></div>
                  <div><small>التكلفة (30 يوماً)</small><b><bdi dir="ltr">{usd(asst.claude.cost_30d_usd)}</bdi></b><em>تقديرية</em></div>
                  <div><small>النموذج</small><b dir="ltr" className="sv-code">{asst.claude.model}</b></div>
                  {asst.claude.price_per_million && (
                    <div><small>السعر لكل مليون كلمة</small><b><bdi dir="ltr">{usd(asst.claude.price_per_million.input)} / {usd(asst.claude.price_per_million.output)}</bdi></b><em>قراءة / كتابة</em></div>
                  )}
                  <div><small>أُجيب من الدليل فقط</small><b>{n(asst.claude.fallback_30d.off + asst.claude.fallback_30d.limit + asst.claude.fallback_30d.error)}</b><em>بلا مفتاح {n(asst.claude.fallback_30d.off)} · تجاوز الحد {n(asst.claude.fallback_30d.limit)} · خطأ {n(asst.claude.fallback_30d.error)}</em></div>
                </div>
                <DayBars days={asst.days} value={(d) => (d.help || 0) + (d.data || 0)} label="سؤال" />

                <h4 className="sv-sub">من يسأل — آخر 30 يوماً</h4>
                <div className="sv-table-wrap">
                  <table className="sv-table">
                    <thead><tr><th>المستخدم</th><th>أسئلة البيانات</th><th>أسئلة الاستخدام</th><th>تكلفة Claude</th><th>آخر سؤال</th></tr></thead>
                    <tbody>
                      {asst.users.map((u) => (
                        <tr key={u.user_id}>
                          <td><b>{u.name || '—'}</b> <small className="sv-muted">{ROLE[u.role] || u.role}</small></td>
                          <td>{n(u.data)}</td>
                          <td>{n(u.help)}</td>
                          <td dir="ltr">{usd(u.cost_usd)}</td>
                          <td>{when(u.last_at)}</td>
                        </tr>
                      ))}
                      {!asst.users.length && <tr><td colSpan={5} className="sv-muted">لا أسئلة خلال 30 يوماً</td></tr>}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </Card>

          {/* ----------------------------------------------------- OneSignal */}
          <Card title={<span className="sv-title"><MdNotificationsActive /> OneSignal — إشعارات الهواتف <Status s={os} /></span>}>
            {os?.configured === false ? (
              <Notice tone="warning" title="مفتاح OneSignal غير مضاف">في مجلد api: <code dir="ltr">npx wrangler secret put ONESIGNAL_REST_API_KEY</code></Notice>
            ) : !os?.ok ? (
              <Notice tone="danger" title="تعذّر الاتصال بـ OneSignal">{os?.error}</Notice>
            ) : (
              <>
                <div className="sv-facts">
                  <div><small>التطبيق</small><b>{os.app.name}</b></div>
                  <div><small>الهواتف المسجّلة</small><b>{n(os.app.players)}</b></div>
                  <div><small>تستقبل الإشعارات الآن</small><b>{n(os.app.messageable_players)}</b></div>
                  <div className={os.app.android_ready ? '' : 'is-bad'}>
                    <small>إعداد أندرويد (Firebase)</small>
                    <b>{os.app.android_ready ? 'جاهز ✓' : 'غير مضبوط'}</b>
                  </div>
                </div>

                <h4 className="sv-sub"><MdPhoneAndroid /> الأجهزة</h4>
                <div className="sv-table-wrap">
                  <table className="sv-table">
                    <thead><tr><th>صاحب الجهاز</th><th>الجهاز</th><th>النظام</th><th>إصدار التطبيق</th><th>مرات الفتح</th><th>آخر نشاط</th><th>يستقبل</th></tr></thead>
                    <tbody>
                      {os.devices.map((d) => (
                        <tr key={d.id}>
                          <td>{d.user_name ? <><b>{d.user_name}</b> <small className="sv-muted">{d.user_role}</small></> : <span className="sv-muted">جهاز من الإصدار السابق</span>}</td>
                          <td dir="ltr" className="sv-code">{d.model || '—'}</td>
                          <td>{d.type} {d.os}</td>
                          <td dir="ltr">{d.app_version || '—'}</td>
                          <td>{n(d.sessions)}</td>
                          <td>{when(d.last_active)}</td>
                          <td>{d.subscribed ? <span className="sv-yes">نعم</span> : <span className="sv-no">لا</span>}</td>
                        </tr>
                      ))}
                      {!os.devices.length && <tr><td colSpan={7} className="sv-muted">لا توجد أجهزة بعد — تظهر عند تسجيل الدخول من التطبيق</td></tr>}
                    </tbody>
                  </table>
                </div>

                <h4 className="sv-sub">آخر الإشعارات المرسلة</h4>
                <div className="sv-table-wrap">
                  <table className="sv-table">
                    <thead><tr><th>العنوان</th><th>وصل</th><th>فشل</th><th>فُتح</th><th>الوقت</th></tr></thead>
                    <tbody>
                      {os.notifications.map((x) => (
                        <tr key={x.id}>
                          <td><b>{x.title || '—'}</b><div className="sv-muted sv-clip">{x.message}</div></td>
                          <td>{n(x.successful)}</td>
                          <td className={x.failed + x.errored ? 'sv-bad' : ''}>{n(x.failed + x.errored)}</td>
                          <td>{n(x.converted)}</td>
                          <td>{when(x.sent_at)}</td>
                        </tr>
                      ))}
                      {!os.notifications.length && <tr><td colSpan={5} className="sv-muted">لم يُرسل أي إشعار للهواتف بعد</td></tr>}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </Card>

          {/* ---------------------------------------------------- ClickSend */}
          <Card title={<span className="sv-title"><MdAccountBalanceWallet /> ClickSend — رسائل الأرقام غير السورية <Status s={cs} /></span>}>
            {cs?.configured === false ? (
              <Notice tone="warning" title="ClickSend غير مربوط">أضف CLICKSEND_USERNAME و CLICKSEND_API_KEY.</Notice>
            ) : !cs?.ok ? (
              <Notice tone="danger" title="تعذّر الاتصال بـ ClickSend">{cs?.error}</Notice>
            ) : (
              <>
                {lowCredit && <Notice tone="danger" title="الرصيد منخفض">اشحن حساب ClickSend حتى لا تتوقف رموز الدخول للأرقام التركية وغيرها.</Notice>}
                <div className="sv-facts">
                  <div className={lowCredit ? 'is-bad' : ''}><small>الرصيد</small><b><bdi dir="ltr">{cs.account.balance.toFixed(2)} {cs.account.currency}</bdi></b><em>تنبيه عند {cs.account.low_credit_amount}</em></div>
                  <div><small>الشحن التلقائي</small><b>{cs.account.auto_recharge ? 'مفعّل' : 'متوقف'}</b></div>
                  <div><small>رسائل مرسلة (الفترة)</small><b>{n(cs.totals.sent)}</b></div>
                  <div><small>التكلفة (الفترة)</small><b><bdi dir="ltr">{cs.totals.cost.toFixed(3)} {cs.account.currency}</bdi></b></div>
                  <div><small>الحساب</small><b>{cs.account.name}</b></div>
                </div>
                <h4 className="sv-sub">آخر الرسائل</h4>
                <div className="sv-table-wrap">
                  <table className="sv-table">
                    <thead><tr><th>إلى</th><th>الحالة</th><th>التكلفة</th><th>الوقت</th></tr></thead>
                    <tbody>
                      {cs.history.map((m, i) => (
                        <tr key={i}>
                          <td dir="ltr">{m.to}</td>
                          <td>{m.status_text || m.status}</td>
                          <td dir="ltr">{m.cost.toFixed(4)}</td>
                          <td>{when(m.sent_at)}</td>
                        </tr>
                      ))}
                      {!cs.history.length && <tr><td colSpan={4} className="sv-muted">لا رسائل</td></tr>}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </Card>

          {/* ---------------------------------------------------------- SMS */}
          <Card title={<span className="sv-title"><MdSms /> الرسائل النصية — من سجل النظام <Status s={sms} /></span>}>
            {!sms?.ok ? (
              <Notice tone="danger" title="تعذّر القراءة">{sms?.error}</Notice>
            ) : (
              <>
                <div className="sv-facts">
                  <div><small>وضع الإرسال</small><b>{sms.sms_mode === 'live' ? 'رسائل حقيقية' : 'تجريبي (بدون إرسال)'}</b></div>
                  <div className={sms.aman_gate_configured ? '' : 'is-bad'}><small>Aman-Gate (الأرقام السورية)</small><b>{sms.aman_gate_configured ? 'مربوط' : 'غير مربوط'}</b><em></em></div>
                </div>
                <h4 className="sv-sub">آخر 30 يوماً حسب المزوّد</h4>
                <div className="sv-table-wrap">
                  <table className="sv-table">
                    <thead><tr><th>المزوّد</th><th>أُرسلت</th><th>فشلت</th><th>أخرى</th><th>رموز دخول</th><th>رسائل وتوثيق</th></tr></thead>
                    <tbody>
                      {sms.last_30d.map((p) => (
                        <tr key={p.provider}>
                          <td dir="ltr" className="sv-code">{p.provider === 'local-dev' ? 'تجريبي' : p.provider}</td>
                          <td>{n(p.sent)}</td>
                          <td className={p.failed ? 'sv-bad' : ''}>{n(p.failed)}</td>
                          <td>{n(p.other)}</td>
                          <td>{n(p.login_codes)}</td>
                          <td>{n(p.messages)}</td>
                        </tr>
                      ))}
                      {!sms.last_30d.length && <tr><td colSpan={6} className="sv-muted">لا رسائل خلال 30 يوماً</td></tr>}
                    </tbody>
                  </table>
                </div>
                {sms.recent.length > 0 && (
                  <>
                    <h4 className="sv-sub">آخر الرسائل (توثيق وإشعارات)</h4>
                    <div className="sv-table-wrap">
                      <table className="sv-table">
                        <thead><tr><th>الرقم</th><th>الغرض</th><th>المزوّد</th><th>الحالة</th><th>الوقت</th></tr></thead>
                        <tbody>
                          {sms.recent.map((r, i) => (
                            <tr key={i}>
                              <td dir="ltr">{r.phone}</td>
                              <td>{r.purpose === 'phone_verify' ? 'توثيق رقم' : r.purpose === 'notification' ? 'إشعار' : r.purpose}</td>
                              <td dir="ltr" className="sv-code">{r.provider || '—'}</td>
                              <td className={r.status === 'failed' ? 'sv-bad' : ''}>{r.status === 'sent' ? 'أُرسلت' : r.status === 'failed' ? 'فشلت' : 'تُخطّيت'}{r.detail ? <div className="sv-muted sv-clip">{r.detail}</div> : null}</td>
                              <td>{when(r.created_at)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </>
            )}
          </Card>
          <p className="sv-muted sv-foot"><MdStorage /> الحدود المعروضة هي حدود الخطة المجانية في Cloudflare.</p>
        </>
      )}
    </PageShell>
  );
}
